/**
 * Config-driven per-surface branding for the ONE shared login screen (`<tb-login-screen>`,
 * tb-login-screen.component.ts) -- hero icon + accent, a real wordmark for the surfaces that own
 * brand art, and an icon+title fallback badge for surfaces that do not.
 *
 * PROMOTED into @toybank/ui (2026-08-18) from toybank-app's login/login-screen.component.ts --
 * owner ruling (2026-08-18): "[the debug wrapper's login and the real toybank.localhost login]
 * need to be the same page, shared fully. The Debug wrapper should not have its own alternate
 * login pages." That ruling covered toybank-app's own two surfaces (the real portal pre-auth
 * screen and the debug wrapper's per-target preview, ADR-0105); this promotion extends the SAME
 * source to the Back-Office Console (`backoffice`), so BOTH apps read ONE config instead of
 * toybank-app owning it alone.
 *
 * Two entries below (`Main Portal` / `Demo Portal`) carry a real wordmark asset that lives only
 * in toybank-app's own `public/brand/` -- left exactly as shipped so toybank-app's rendering
 * (including the debug wrapper's per-target preview, which mounts this SAME config) stays
 * byte-identical after the promotion. `Backoffice` / `Demo Backoffice` intentionally have
 * NO wordmark here (same icon+title badge fallback as before promotion) for the identical reason:
 * the backoffice wordmark asset (`back-of-antarctica-wordmark-light.svg`) lives only in
 * backoffice's own `public/brand/`, so wiring it into this shared entry would 404 when the
 * toybank-app debug wrapper mounts the same config to preview the "backoffice" target. Backoffice
 * wires its OWN wordmark locally (`app.component.ts`'s `loginPresentation`) by spreading these
 * base entries and adding the asset path that is genuinely reachable from its own document root --
 * still "the same config" for icon/title/subtitle/accent, just not a shared image URL across two
 * separate SPA origins.
 *
 * The `heroSrc` landing artwork (added 2026-08-22) does NOT repeat that compromise. Rather than
 * living in one app's `public/` and 404-ing from the other, the five images have their own shared
 * source of truth at `projects/ui/assets/brand/login/`, which BOTH apps copy to the same output
 * path (`brand/login/`) via an `assets` entry in angular.json. One file on disk, one URL, reachable
 * from every origin that mounts this config -- including the debug wrapper previewing a target that
 * is not its own. Note that entry had to be added to all EIGHT assets arrays, not just the two
 * build defaults: a per-configuration `assets` block REPLACES the base array rather than merging
 * with it, so `sandbox` / `debug` / `debug-host` / `development` each need their own copy or the
 * artwork silently 404s in exactly the shells that preview other surfaces.
 */
export interface LoginPresentation {
  readonly icon: string;
  readonly title: string;
  readonly subtitle: string;
  readonly accent: string;
  /**
   * Wordmark asset for a DARK surface (light ink). Kept for back-compat with anything still
   * reading a single `wordmarkSrc` -- the component renders BOTH variants (theme-swapped, see
   * `TbThemeImageComponent`) once `wordmarkSrcLight` is also supplied, and falls back to this one
   * asset unconditionally when it is not (2026-09-20 light-mode visibility fix).
   */
  readonly wordmarkSrc?: string;
  /** Wordmark asset for a LIGHT surface (dark ink). Renders in place of `wordmarkSrc` once the
   *  resolved theme is light (see `TbThemeImageComponent`'s [data-theme]/prefers-color-scheme
   *  cascade). Optional so presentations that only ever render against one fixed background
   *  (none today) can still supply a single `wordmarkSrc`. */
  readonly wordmarkSrcLight?: string;
  readonly wordmarkAlt?: string;
  /**
   * Landing artwork for the hero panel. Optional: a surface without one falls back to the
   * accent-gradient panel, which is what every surface used to get (see the component).
   */
  readonly heroSrc?: string;
  readonly heroAlt?: string;
}

const CASHBRO_WORDMARK = {
  wordmarkSrc: 'brand/bank-of-antarctica-cashbro-wordmark-light.svg',
  wordmarkSrcLight: 'brand/bank-of-antarctica-cashbro-wordmark.svg',
  wordmarkAlt: 'Bank of Antarctica: CashBro',
} as const;

export const LOGIN_PRESENTATION: Readonly<Record<string, LoginPresentation>> = {
  'Main Portal': {
    icon: 'account_balance',
    title: 'CashBro',
    subtitle: "Bank of Antarctica's commercial banking portal.",
    accent: 'var(--tb-brand-accent, #4f86f7)',
    heroSrc: 'brand/login/main-portal.png',
    heroAlt: 'The Bank of Antarctica headquarters campus at blue hour, across a frozen plaza.',
    ...CASHBRO_WORDMARK,
  },
  'Backoffice': {
    // Owner icon pass 2026-08-19: support_agent moved here from Demo Backoffice.
    icon: 'support_agent',
    title: 'Back of Antarctica',
    subtitle: 'Staff console sign-in.',
    accent: 'var(--tb-status-info, #7aa2f7)',
    heroSrc: 'brand/login/backoffice.png',
    heroAlt: 'A back-of-house service corridor inside the bank, opening onto polar light.',
  },
  'Demo Portal': {
    // Owner icon pass 2026-08-19: person-with-lightning-bolt = Zeus (the demo's Olympus cast).
    icon: 'recent_patient',
    title: 'CashBro',
    subtitle: "Bank of Antarctica's commercial banking portal — demo data.",
    accent: 'var(--tb-finance-pending, #e0af68)',
    heroSrc: 'brand/login/demo-portal.png',
    heroAlt: 'A scale architectural model of the Bank of Antarctica campus, lit on a studio table.',
    ...CASHBRO_WORDMARK,
  },
  'Demo Backoffice': {
    icon: 'supervisor_account',
    title: 'Back of Antarctica',
    subtitle: 'Staff console sign-in — demo data.',
    accent: 'var(--tb-finance-pending, #e0af68)',
    heroSrc: 'brand/login/demo-backoffice.png',
    heroAlt: 'A cutaway scale model of a bank operations floor, lit on a studio table.',
  },
  Simulator: {
    icon: 'location_city',
    title: 'TownSim',
    subtitle: 'Enter the simulated economy sandbox.',
    accent: 'var(--tb-ai-accent, #7C3AED)',
    heroSrc: 'brand/login/simulator.png',
    heroAlt: 'A tabletop planning model of a small snow-covered town and its frozen harbour.',
  },
};

export const DEFAULT_LOGIN_PRESENTATION = LOGIN_PRESENTATION['Main Portal'];

/**
 * The injected divergence point (owner ruling 2026-08-18): the ONLY thing that may differ between
 * one login surface and another is WHAT clicking the primary action does.
 *  - toybank-app real portal: navigate to Keycloak, optionally carrying the typed username as an
 *    OIDC `login_hint`.
 *  - toybank-app debug wrapper: POST /bff/debug/login (or a cross-origin handoff) for the
 *    selected seeded actual user.
 *  - backoffice: navigate to Keycloak (its own `/oauth2/authorization/keycloak`), same pattern as
 *    the real portal.
 */
export interface LoginSubmitStrategy {
  submit(): void;
}
